import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ReactComponent as RadioOn } from '../../assets/images/svg/radio-on.svg';
import { ReactComponent as RadioOff } from '../../assets/images/svg/radio-off.svg';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import themeColors from '../../assets/styles/themeColors';
import { uFlexCenter } from '../../assets/styles/utility';
import { variables } from '../../assets/styles/variables';

const getRadioVariantStyles = (variant, selected) => {
  if (variant === 'RadioRounded') {
    return `
    border: 1px solid ${
      selected ? themeColors.colorFocus : themeColors.colorInputBorder
    };
    padding: ${pxToRem(6)} ${pxToRem(16)} ${pxToRem(12)};

    svg {
      margin-top: ${pxToRem(4)};
    }
    `;
  }

  if (variant === 'RadioBox') {
    return `
    box-shadow: ${variables.shadow.boxShadow};
    align-items: center;
    border: 1px solid ${selected ? themeColors.colorFocus : 'transparent'};
    padding: ${pxToRem(16)};
    `;
  }
};

const getRadioContentVariantStyles = (variant) => {
  if (variant === 'RadioBox') {
    return `
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;

        ${mediaBelow(variables.breakpoints.bpMd)} {
          flex-direction: column;
          align-items: flex-start;
        }
    `;
  }
};

export const RadioWrap = styled.label`
  display: flex;
  cursor: pointer;
  border-radius: ${variables.borderRadius.borderRadius};
  ${({ disabled }) =>
    disabled &&
    `
  opacity: 0.5;
  cursor: not-allowed;
`}}

  ${({ variant, selected }) => getRadioVariantStyles(variant, selected)}
`;

// CSS-drawn alternative to the radio-on/radio-off SVGs, for callers that need
// a thicker outline than the SVGs allow — their 1px stroke and #003246 colour
// are baked into the asset, so neither can be themed or overridden. Size and
// border are caller-supplied CSS values: pass absolute px to opt out of the
// root-font-size scaling that rem values (RadioIndicator's own box included)
// are subject to. Palette mirrors CheckboxGTT's `thickBorder`.
export const RadioCircle = styled.span`
  box-sizing: border-box;
  flex-shrink: 0;
  width: ${({ circleWidth }) => circleWidth || '16px'};
  height: ${({ circleHeight }) => circleHeight || '16px'};
  border-radius: ${({ circleBorderRadius }) => circleBorderRadius};
  position: relative;
  border: ${({ circleBorderWidth }) => circleBorderWidth} solid
    ${({ selected }) =>
      selected ? themeColors.colorPrimary : themeColors.colorTextSecondary};
  ${({ disabled }) => disabled && `opacity: 0.5;`}

  &::after {
    content: '';
    display: ${({ selected }) => (selected ? 'block' : 'none')};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    /* Percentages here resolve against the padding box (border excluded), so
       half of the visible border-box needs the border added back in to land
       on an exact half of circleWidth/circleHeight. */
    width: calc(50% + ${({ circleBorderWidth }) => circleBorderWidth});
    height: calc(50% + ${({ circleBorderWidth }) => circleBorderWidth});
    border-radius: 50%;
    background-color: ${themeColors.colorPrimary};
  }
`;

// The box reserved for the indicator. Absolute px (not rem) so it matches
// RadioCircle's absolute size at every breakpoint — a rem box would shrink
// under the smaller root font-size and squash or clip the circle. Callers
// sizing the circle differently get a box that tracks it. The SVG path keeps
// its original rem sizing.
export const RadioIndicator = styled.div`
  ${uFlexCenter};
  width: ${({ circleWidth }) => circleWidth || '16px'};
  height: ${({ circleHeight }) => circleHeight || '16px'};
  margin-right: ${pxToRem(16)};

  svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
    ${({ disabled }) =>
      disabled &&
      `
      opacity: 0.5;
    `}}
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    svg {
      width: ${pxToRemMd(16)};
      height: ${pxToRemMd(16)};
    }
  }
`;

export const RadioContent = styled.div`
  font-size: ${pxToRem(14)};
  line-height: 1.15;
  color: ${themeColors.colorLabel};
  user-select: none;
  margin-right: 1rem;
  ${({ variant }) => getRadioContentVariantStyles(variant)}
  ${({ removeCheckbox }) => removeCheckbox && `margin-left: 2rem;`}
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: ${pxToRemMd(14)};
  }
  width: 100%;
`;

export const RadioField = styled.input`
  display: none;
`;

const Radio = ({
  children,
  name,
  checked,
  field,
  value,
  selected,
  id = 'radio',
  notCenteredIndicator,
  variant = 'Radio',
  disabled,
  removeCheckbox,
  cssIndicator,
  circleWidth = '16px',
  circleHeight = '16px',
  circleBorderRadius = '50%',
  circleBorderWidth = '2px',
  ...props
}) => (
  <RadioWrap
    htmlFor={name}
    notCenteredIndicator={notCenteredIndicator}
    selected={selected}
    variant={variant}
    disabled={disabled}
    {...props}
  >
    {!removeCheckbox && (
      <>
        <RadioField
          name={name}
          id={id}
          type="radio"
          checked={checked}
          value={value}
          disabled={disabled ?? false}
          {...field}
        />
        <RadioIndicator
          disabled={disabled}
          circleWidth={cssIndicator ? circleWidth : undefined}
          circleHeight={cssIndicator ? circleHeight : undefined}
        >
          {cssIndicator && (
            <RadioCircle
              selected={selected}
              disabled={disabled}
              circleWidth={circleWidth}
              circleHeight={circleHeight}
              circleBorderRadius={circleBorderRadius}
              circleBorderWidth={circleBorderWidth}
            />
          )}
          {!cssIndicator && (selected ? <RadioOn /> : <RadioOff />)}
        </RadioIndicator>
      </>
    )}
    <RadioContent variant={variant} removeCheckbox={removeCheckbox}>
      {children}
    </RadioContent>
  </RadioWrap>
);

Radio.propTypes = {
  children: PropTypes.node,
  checked: PropTypes.bool,
  name: PropTypes.string,
  field: PropTypes.shape({}),
  form: PropTypes.shape({}),
  className: PropTypes.string,
  value: PropTypes.string,
  selected: PropTypes.bool,
  notCenteredIndicator: PropTypes.bool,
  id: PropTypes.string,
  variant: PropTypes.oneOf(['RadioBox', 'RadioRounded', 'Radio']),
  disabled: PropTypes.bool,
  removeCheckbox: PropTypes.bool,
  // Opt-in: draw the indicator in CSS (outline + dot) instead of the
  // radio-on/radio-off SVGs, whose 1px stroke can't be thickened. Used by
  // SelectWeightDrawer's "All Weights" radio so it matches the 2px weight
  // checkboxes below it; every other caller keeps the SVGs.
  cssIndicator: PropTypes.bool,
  // Geometry of the CSS indicator, as CSS values — absolute px keeps it out
  // of the root-font-size scaling rem values are subject to. Ignored unless
  // cssIndicator is set.
  circleWidth: PropTypes.string,
  circleHeight: PropTypes.string,
  circleBorderRadius: PropTypes.string,
  circleBorderWidth: PropTypes.string,
};

export default Radio;
